import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services";
import { clearAccessToken } from "@/lib/apiClient";
import { buildSignInUrl, getCurrentRelativeUrl, saveAuthIntent } from "@/lib/authRedirect";
import type { UserDto } from "@/services/types";
import { addUnauthorizedListener } from "@/lib/authEvents";
import { authDebug, routeDebug } from "@/lib/authDebug";

interface AuthContextValue {
  user: UserDto | null;
  loading: boolean;
  isHydratingAuth: boolean;
  authInitialized: boolean;
  logoutLoading: boolean;
  setUser: React.Dispatch<React.SetStateAction<UserDto | null>>;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
export const ME_QUERY_KEY = ["me"] as const;

export function getMeQueryOptions() {
  return {
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      authDebug("/api/me request start");
      const me = await api.getMe();
      authDebug("/api/me request success", { hasUser: Boolean(me?.id) });
      return me;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // Refetch on focus once the data is stale (>10 min old). This is how a tab left open picks
    // up an entitlement change made out of band — an admin comp grant, a webhook-driven
    // downgrade, a plan change in another tab — none of which invalidate ["me"] here. The
    // checkout-return flow still invalidates explicitly for an immediate refresh; this is the
    // slow-path safety net. The refetch runs with skipGlobalLoader (see AuthProvider), so it
    // never flashes the app loader.
    refetchOnWindowFocus: true as const,
    retry: false as const,
  };
}

function isProtectedPath(path: string) {
  return path.startsWith("/dashboard")
    || path.startsWith("/settings")
    || path.startsWith("/availability")
    || path.startsWith("/bookings")
    || path.startsWith("/integrations")
    || path.startsWith("/create-event/")
    || path.startsWith("/onboarding")
    || path.startsWith("/invitations/");
}

function shouldBlockInitialAuthHydration(path: string) {
  return isProtectedPath(path)
    || path === "/sign-in"
    || path === "/login";
}

function isPublicBookingPath(path: string) {
  if (/^\/manage\/[^/]+$/.test(path)) return true;
  if (/^\/(book|public)\/[^/]+\/[^/]+(?:\/manage\/[^/]+)?$/.test(path)) return true;

  const segments = path.split("/").filter(Boolean);
  if (segments.length < 2 || segments.length > 4) return false;

  const reservedFirstSegments = new Set([
    "about",
    "availability",
    "book",
    "bookings",
    "contact",
    "create-event",
    "d",
    "dashboard",
    "docs",
    "integrations",
    "invitations",
    "login",
    "manage",
    "onboarding",
    "pricing",
    "privacy",
    "public",
    "settings",
    "sign-in",
    "terms",
  ]);

  if (reservedFirstSegments.has(segments[0])) return false;
  if (segments.length === 2) return true;
  return segments.length === 4 && segments[2] === "manage";
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initialAuthResolvedRef = useRef(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [initialAuthResolved, setInitialAuthResolved] = useState(false);
  const shouldHydrateAuth = !isPublicBookingPath(location.pathname) || shouldBlockInitialAuthHydration(location.pathname);

  const meQuery = useQuery({
    ...getMeQueryOptions(),
    enabled: shouldHydrateAuth,
    queryFn: async () => {
      authDebug("/api/me request start");
      const me = await api.getMe(
        initialAuthResolvedRef.current
          ? { skipGlobalLoader: true }
          : { loadingType: shouldBlockInitialAuthHydration(location.pathname) ? "user-blocking" : "background" },
      );
      authDebug("/api/me request success", { hasUser: Boolean(me?.id) });
      return me;
    },
  });

  useEffect(() => {
    initialAuthResolvedRef.current = initialAuthResolved;
  }, [initialAuthResolved]);

  useEffect(() => {
    if (!shouldHydrateAuth) {
      if (!initialAuthResolved) setInitialAuthResolved(true);
      return;
    }
    if (!initialAuthResolved && meQuery.status !== "pending") {
      setInitialAuthResolved(true);
    }
  }, [initialAuthResolved, meQuery.status, shouldHydrateAuth]);

  const user = meQuery.data ?? null;
  const loading = shouldHydrateAuth && meQuery.isPending && !meQuery.data;
  const isHydratingAuth = shouldHydrateAuth && !initialAuthResolved && loading;
  const authInitialized = !shouldHydrateAuth || initialAuthResolved || meQuery.status !== "pending";

  const setUser = useCallback<React.Dispatch<React.SetStateAction<UserDto | null>>>((next) => {
    queryClient.setQueryData<UserDto | null>(["me"], (prev) => {
      const resolved = typeof next === "function" ? next(prev ?? null) : next;
      return resolved;
    });
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    authDebug("refreshUser start", { path: getCurrentRelativeUrl() });
    const result = await meQuery.refetch();
    authDebug("refreshUser success", { userRestored: Boolean(result.data) });
  }, [meQuery]);

  useEffect(() => {
    if (!meQuery.data || !meQuery.isStale || meQuery.isFetching) return;
    void meQuery.refetch();
  }, [location.hash, location.pathname, location.search, meQuery.data, meQuery.isFetching, meQuery.isStale, meQuery.refetch]);

  useEffect(() => {
    const onUnauthorized = () => {
      const redirectTarget = getCurrentRelativeUrl();
      routeDebug("unauthorized event received", { path: redirectTarget, authInitialized });
      // On public/login pages a 401 from /api/me is the normal logged-out state.
      // Clearing the query cache here resets the `me` query to pending, which
      // refetches /api/me, 401s again, and re-fires this handler — an infinite
      // loop that renders the sign-in page blank. Bail out before touching state.
      if (!isProtectedPath(redirectTarget)) {
        routeDebug("skipping redirect for public path", { redirectTarget });
        return;
      }
      clearAccessToken();
      // Clear all cached queries so no authenticated data lingers after session expiry.
      queryClient.clear();
      saveAuthIntent({ mode: "PROTECTED_ROUTE", returnTo: redirectTarget });
      routeDebug("redirecting to protected sign-in", { redirectTarget });
      navigate(buildSignInUrl({ mode: "PROTECTED_ROUTE", returnTo: redirectTarget }), { replace: true });
    };
    return addUnauthorizedListener(onUnauthorized);
  }, [authInitialized, navigate, queryClient]);

  const logout = useCallback(async () => {
    setLogoutLoading(true);
    try {
      await api.logout();
    } catch (error) {
      console.error("Logout request failed, forcing frontend logout", error);
    } finally {
      clearAccessToken();
      queryClient.clear();
      setLogoutLoading(false);
      navigate("/", { replace: true });
    }
  }, [navigate, queryClient]);

  const value = useMemo(
    () => ({
      user,
      loading,
      isHydratingAuth,
      authInitialized,
      logoutLoading,
      setUser,
      refreshUser,
      logout,
    }),
    [authInitialized, isHydratingAuth, loading, logout, logoutLoading, refreshUser, setUser, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

/**
 * The auth context when there is one, otherwise null — no throw.
 *
 * <p>For components shared with the embed widget. The widget renders on someone else's website
 * with no BunnyCal session and deliberately mounts no AuthProvider, so a component that merely
 * *prefills* from a signed-in user must be able to ask and be told "no one". Application code
 * should keep using {@link useAuth}, whose throw catches a genuinely missing provider.
 */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
